import React from "react";

// Shared by the stim table and the confirmation page so there is one place that decides how
// a stim gets rendered.
//
// Pointing an iframe at a stim source is what downloads it: if the browser cannot render the
// response inline - an archive, or anything served with Content-Disposition: attachment - the
// iframe turns into a download. So pick the element from the media type, and sandbox the
// iframe fallback, which blocks downloads outright.
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "avif", "bmp", "svg"];
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogv", "ogg", "mov", "m4v"];
// nothing a browser renders inline - offering a frame for these only ever downloads them
const NON_RENDERABLE_EXTENSIONS = ["zip", "gz", "tgz", "tar", "rar", "7z", "doc", "docx", "xls", "xlsx", "ppt", "pptx"];

function extensionOf(source) {
    if (!source) {
        return "";
    }

    // drop the query and hash first, or ".html?step_id=5" reads as an extension of "html?step_id=5"
    const path = String(source).split(/[?#]/)[0];
    const lastSegment = path.split("/").pop() || "";
    const dot = lastSegment.lastIndexOf(".");

    return dot === -1 ? "" : lastSegment.slice(dot + 1).toLowerCase();
}

export function previewKindFor(item) {
    if (!item) {
        return "none";
    }

    // a thumbnail is always safe to render, so prefer it over the real source
    if (item.low_quality_source) {
        return "image";
    }

    if (!item.source) {
        return "none";
    }

    const extension = extensionOf(item.source);

    // stim_type === "video" is the convention used across the reports and KPI pages
    if (String(item.stim_type).toLowerCase() === "video" || VIDEO_EXTENSIONS.includes(extension)) {
        return "video";
    }

    if (IMAGE_EXTENSIONS.includes(extension)) {
        return "image";
    }

    if (NON_RENDERABLE_EXTENSIONS.includes(extension)) {
        return "unavailable";
    }

    return "frame";
}

export default function StimPreview({ item, width, height }) {
    const kind = previewKindFor(item);

    const baseStyle = {
        width,
        height,
        border: 0,
        display: "block",
        background: "#fff",
    };

    if (kind === "image") {
        return (
            <img
                src={item.low_quality_source || item.source}
                alt={item.name || "stim preview"}
                style={{ ...baseStyle, objectFit: "contain" }}
            />
        );
    }

    if (kind === "video") {
        return (
            // preload metadata only - preload="auto" would pull the whole file down
            <video
                src={item.source}
                style={{ ...baseStyle, objectFit: "contain" }}
                controls
                preload="metadata"
            />
        );
    }

    if (kind === "unavailable" || kind === "none") {
        return (
            <div
                style={{
                    ...baseStyle,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: 12,
                    textAlign: "center",
                    color: "rgb(99, 115, 129)",
                    fontSize: 13,
                    background: "rgb(244, 246, 248)",
                }}
            >
                <span>no inline preview for this stim</span>
                {item && item.source ? (
                    <a href={item.source} target="_blank" rel="noopener noreferrer">
                        open in new tab
                    </a>
                ) : null}
            </div>
        );
    }

    return (
        // sandbox with no allow-downloads token is what stops a stim downloading when the
        // response turns out to be an attachment. allow-scripts and allow-same-origin keep
        // ad tags rendering - safe together here because the stim is served from another
        // origin, so it can never reach this document.
        <iframe
            src={item.source}
            title={item.name || "stim preview"}
            style={baseStyle}
            sandbox="allow-scripts allow-same-origin"
        />
    );
}
